import { useEffect, useRef } from "react";
import { focusManager, onlineManager } from "@tanstack/react-query";

/**
 * For live data that lives outside the query cache (the notification bell's
 * store): refetch when the tab becomes visible again or the network comes
 * back — the same moments queries revalidate — on top of the socket push.
 * No interval polling. Uses TanStack's focus/online managers, which follow
 * page visibility (not window focus), so a native file-picker dialog
 * opening and closing never triggers a refetch.
 */
/** A tab switch refetches at most once per this window (connection plan step 4):
 *  the socket already delivers new items live, and a reconnect resyncs. */
export const FOCUS_REFETCH_MIN_INTERVAL_MS = 30_000;

export function useRefetchOnFocusAndReconnect(refetch: () => void): void {
  const ref = useRef(refetch);
  ref.current = refetch;
  const lastFocusRefetch = useRef(0);
  useEffect(() => {
    const offFocus = focusManager.subscribe((focused) => {
      if (!focused || Date.now() - lastFocusRefetch.current < FOCUS_REFETCH_MIN_INTERVAL_MS) return;
      lastFocusRefetch.current = Date.now();
      ref.current();
    });
    const offOnline = onlineManager.subscribe((online) => {
      if (online) ref.current();
    });
    return () => {
      offFocus();
      offOnline();
    };
  }, []);
}
